import { useEffect } from 'react';
import { TreeApi } from 'react-arborist';
import { SectionsTreeNode } from '@repo/atomic-components';

interface UseTreeDragAutoscrollOptions<T extends SectionsTreeNode> {
  treeApiRef: React.RefObject<TreeApi<T> | null>;
  // Distance from the top/bottom edge (in pixels) that starts scrolling.
  edgeZonePx?: number;
  // Max scroll speed at the very edge, in pixels per animation frame.
  maxSpeedPx?: number;
}

/**
 * Scrolls react-arborist's virtualised list up or down while the user is
 * dragging near its top or bottom edge. react-arborist itself doesn't
 * implement drag autoscroll, so nested drops just fail to reach the item
 * they're aimed at. We attach a passive dragover listener to its scroll
 * element and adjust scrollTop each frame the cursor stays in the edge
 * zone — speed scales linearly with proximity to the edge, capped at
 * `maxSpeedPx`. Frames stop as soon as the drag ends or leaves the zone.
 */
export const useTreeDragAutoscroll = <T extends SectionsTreeNode>({
  treeApiRef,
  edgeZonePx = 48,
  maxSpeedPx = 16,
}: UseTreeDragAutoscrollOptions<T>): void => {
  useEffect(() => {
    let scrollEl: HTMLDivElement | null = null;
    let attachRafId = 0;
    let scrollRafId = 0;
    let currentSpeed = 0;

    const tickScroll = () => {
      scrollRafId = 0;
      if (!scrollEl || currentSpeed === 0) return;
      const before = scrollEl.scrollTop;
      scrollEl.scrollTop = before + currentSpeed;
      // Only keep the frame loop running while we're still moving —
      // otherwise a stopped-at-edge drag would burn CPU indefinitely.
      if (scrollEl.scrollTop !== before) {
        scrollRafId = requestAnimationFrame(tickScroll);
      } else {
        currentSpeed = 0;
      }
    };

    const scheduleTick = () => {
      if (scrollRafId === 0 && currentSpeed !== 0) {
        scrollRafId = requestAnimationFrame(tickScroll);
      }
    };

    const computeSpeed = (clientY: number): number => {
      if (!scrollEl) return 0;
      const rect = scrollEl.getBoundingClientRect();
      const fromTop = clientY - rect.top;
      const fromBottom = rect.bottom - clientY;
      if (fromTop < edgeZonePx) {
        // Ramp from 0 at zone-edge to -maxSpeed at the very top.
        const factor = Math.max(0, Math.min(1, (edgeZonePx - fromTop) / edgeZonePx));
        return -Math.round(maxSpeedPx * factor);
      }
      if (fromBottom < edgeZonePx) {
        const factor = Math.max(0, Math.min(1, (edgeZonePx - fromBottom) / edgeZonePx));
        return Math.round(maxSpeedPx * factor);
      }
      return 0;
    };

    const onDragOver = (e: DragEvent) => {
      const nextSpeed = computeSpeed(e.clientY);
      if (nextSpeed !== currentSpeed) {
        currentSpeed = nextSpeed;
        scheduleTick();
      }
    };

    const stopScrolling = () => {
      currentSpeed = 0;
      if (scrollRafId) {
        cancelAnimationFrame(scrollRafId);
        scrollRafId = 0;
      }
    };

    const attach = () => {
      const el = treeApiRef.current?.listEl.current;
      if (el) {
        scrollEl = el;
        el.addEventListener('dragover', onDragOver);
        el.addEventListener('dragleave', stopScrolling);
        el.addEventListener('drop', stopScrolling);
        document.addEventListener('dragend', stopScrolling);
      } else {
        attachRafId = requestAnimationFrame(attach);
      }
    };
    attach();

    return () => {
      cancelAnimationFrame(attachRafId);
      cancelAnimationFrame(scrollRafId);
      if (scrollEl) {
        scrollEl.removeEventListener('dragover', onDragOver);
        scrollEl.removeEventListener('dragleave', stopScrolling);
        scrollEl.removeEventListener('drop', stopScrolling);
      }
      document.removeEventListener('dragend', stopScrolling);
    };
  }, [treeApiRef, edgeZonePx, maxSpeedPx]);
};
